import { useState } from 'react';
import axios from 'axios';
import { useAgents } from '../hooks/useAgents';
import type { Agent } from '../data/agents';

interface RewriteModalProps {
  isOpen: boolean;
  selectedText: string;
  onClose: () => void;
  onAccept: (rewrittenText: string) => void;
  apiEndpoint?: string;
}

export default function RewriteModal({ isOpen, selectedText, onClose, onAccept, apiEndpoint = '/api/gdriveeditor/documents/rewrite' }: RewriteModalProps) {
  const { agents, customPrompts, getAgentPrompt, updatePrompt, resetPrompt } = useAgents();

  const [rewritePrompt, setRewritePrompt] = useState('');
  const [rewritePreview, setRewritePreview] = useState<string | null>(null);
  const [agentSearch, setAgentSearch] = useState('');
  const [isRewriting, setIsRewriting] = useState(false);
  const [error, setError] = useState('');

  // Edit agent modal state
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [editedPrompt, setEditedPrompt] = useState('');

  const filteredAgents = agents.filter(
    a => a.name.toLowerCase().includes(agentSearch.toLowerCase()) ||
         a.description.toLowerCase().includes(agentSearch.toLowerCase())
  );

  const handleRewriteSubmit = async () => {
    if (!rewritePrompt.trim() || !selectedText.trim()) return;
    setIsRewriting(true); setError(''); setRewritePreview(null);
    try {
      const result = await axios.post(apiEndpoint, {
        selected_text: selectedText,
        prompt: rewritePrompt,
      }, { headers: { 'Content-Type': 'application/json' } });
      if (result.data?.rewritten) {
        setRewritePreview(result.data.rewritten);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to rewrite text');
    } finally { setIsRewriting(false); }
  };

  const handleAcceptRewrite = () => {
    if (!rewritePreview) return;
    onAccept(rewritePreview);
    resetState();
  };

  const handleRejectRewrite = () => {
    setRewritePreview(null);
  };

  const handleRetryRewrite = () => {
    setRewritePreview(null);
    handleRewriteSubmit();
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const resetState = () => {
    setRewritePrompt('');
    setRewritePreview(null);
    setAgentSearch('');
    setError('');
    setEditingAgent(null);
    setEditedPrompt('');
  };

  const handleSaveAgentPrompt = () => {
    if (!editingAgent) return;
    updatePrompt(editingAgent.name, editedPrompt);
    setEditingAgent(null);
    setEditedPrompt('');
  };

  const handleResetAgentPrompt = () => {
    if (!editingAgent) return;
    resetPrompt(editingAgent.name);
    setEditedPrompt(editingAgent.prompt);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Rewrite Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800">
              {rewritePreview ? 'Review Rewrite Result' : 'Rewrite Selected Text'}
            </h3>
          </div>

          <div className="p-6 space-y-4 overflow-y-auto flex-1">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
            )}

            {rewritePreview ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-red-600 mb-2">Original</label>
                    <div className="p-3 bg-red-50 rounded-lg border border-red-200 max-h-72 overflow-y-auto">
                      <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">{selectedText}</pre>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-green-600 mb-2">Rewritten</label>
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200 max-h-72 overflow-y-auto">
                      <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">{rewritePreview}</pre>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-400 text-center">Review the result above. Accept to apply, Reject to go back and edit your prompt, or Retry to regenerate.</p>
              </>
            ) : (
              <>
                {/* Selected text preview */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Selected Text ({selectedText.length} characters)</label>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 max-h-48 overflow-y-auto">
                    <pre className="text-sm text-gray-600 whitespace-pre-wrap font-sans">{selectedText}</pre>
                  </div>
                </div>
                {/* Prompt input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Rewrite Instructions</label>
                  <textarea value={rewritePrompt} onChange={e => setRewritePrompt(e.target.value)}
                    placeholder="E.g., Make it more concise, Fix grammar, Make it more formal..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    rows={3} autoFocus />
                </div>
                {/* Agent selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">Select Agent / Quick Action</label>
                  <div className="relative mb-2">
                    <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                    <input type="text" value={agentSearch} onChange={e => setAgentSearch(e.target.value)} placeholder="Search agents..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm" />
                    {agentSearch && (
                      <button onClick={() => setAgentSearch('')} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                      </button>
                    )}
                  </div>
                  <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                    {filteredAgents.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-500 text-center">No agents found matching "{agentSearch}"</div>
                    ) : (
                      filteredAgents.map(agent => {
                        const ep = getAgentPrompt(agent);
                        const isCustomized = customPrompts[agent.name] !== undefined;
                        const isUserAdded = 'isCustom' in agent && agent.isCustom;
                        return (
                          <div key={agent.name} className={`flex items-center border-b border-gray-100 last:border-b-0 ${rewritePrompt === ep ? 'bg-purple-100' : ''}`}>
                            <button onClick={() => setRewritePrompt(ep)} className="flex-1 px-4 py-2 text-left hover:bg-purple-50 transition-colors">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm text-gray-800">{agent.name}</span>
                                {isUserAdded && <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-600 rounded">User</span>}
                                {isCustomized && <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-600 rounded">Custom</span>}
                              </div>
                              <div className="text-xs text-gray-500 truncate">{agent.description}</div>
                            </button>
                            <button onClick={e => { e.stopPropagation(); setEditingAgent(agent); setEditedPrompt(getAgentPrompt(agent)); }}
                              className="p-2 mr-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors" title="Edit agent prompt">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            </button>
                          </div>
                        );
                      })
                    )}
                  </div>
                  <div className="mt-1 text-xs text-gray-400">{filteredAgents.length} of {agents.length} agents</div>
                </div>
              </>
            )}
          </div>

          {/* Footer buttons */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            {rewritePreview ? (
              <>
                <button onClick={handleRejectRewrite}
                  className="px-4 py-2 text-red-700 bg-red-100 rounded-lg hover:bg-red-200 transition-colors flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  Reject
                </button>
                <button onClick={handleRetryRewrite} disabled={isRewriting}
                  className="px-4 py-2 text-orange-700 bg-orange-100 rounded-lg hover:bg-orange-200 transition-colors disabled:opacity-50 flex items-center gap-2">
                  {isRewriting ? (
                    <><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>Retrying...</>
                  ) : (
                    <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>Retry</>
                  )}
                </button>
                <button onClick={handleAcceptRewrite}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  Accept
                </button>
              </>
            ) : (
              <>
                <button onClick={handleClose} disabled={isRewriting}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50">Cancel</button>
                <button onClick={handleRewriteSubmit} disabled={!rewritePrompt.trim() || isRewriting}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center gap-2">
                  {isRewriting ? (
                    <><svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>Rewriting...</>
                  ) : 'Rewrite'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Edit Agent Modal */}
      {editingAgent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Edit Agent: {editingAgent.name}</h3>
              <p className="text-sm text-gray-500 mt-1">{editingAgent.description}</p>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Agent Prompt</label>
              <textarea value={editedPrompt} onChange={e => setEditedPrompt(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none font-mono text-sm"
                rows={12} placeholder="Enter the agent prompt..." />
              {customPrompts[editingAgent.name] && (
                <p className="mt-2 text-xs text-blue-600">This agent has a custom prompt. Click "Reset to Default" to restore the original.</p>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
              <button onClick={handleResetAgentPrompt} className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm transition-colors">Reset to Default</button>
              <div className="flex gap-3">
                <button onClick={() => { setEditingAgent(null); setEditedPrompt(''); }} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Cancel</button>
                <button onClick={handleSaveAgentPrompt} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">Save Prompt</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
